'use client';

import {
    Box,
    BoxContent,
    DirectionEnum,
    FlexBox,
    FlexItem,
    GapSize,
    Icon,
    IconName,
    IconSize,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyAlign,
    TypographyBold,
} from '@bytel/trilogy-react';

import { EasyTradeInResult, EasyTradeInStatus } from '@app-types/easy-trade-in';

import { InlinePrice } from '@components/inline-price';

interface EasyTradeInConfirmedContentProps {
    result: EasyTradeInResult;
    isTradeInConfirmed?: boolean;
}

export function EasyTradeInConfirmedContent({
    result,
    isTradeInConfirmed = false,
}: Readonly<EasyTradeInConfirmedContentProps>) {
    if (result.status === EasyTradeInStatus.SETTLED) {
        return (
            <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                <div>
                    <div className='has-text-centered'>
                        <Icon name={IconName.CONFETTI_CANNON} size={IconSize.MEDIUM} />
                    </div>
                    <Text
                        level={TextLevels.ONE}
                        typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyAlign.TEXT_CENTERED]}
                    >
                        Le crédit sur votre téléphone sera clôturé sans frais supplémentaires&nbsp;!
                    </Text>
                </div>
                <FlexBox>
                    <InfoBox
                        title={
                            isTradeInConfirmed
                                ? 'Crédit restant à payer sur le téléphone\u00A0: '
                                : 'Crédit restant à payer sur le téléphone (après reprise)\u00A0:'
                        }
                        iconName={IconName.MOBILE}
                        price={result.remainingCreditTerminal}
                    />
                    {result.creditAccessories > 0 && (
                        <InfoBox
                            title='Crédit restant à payer sur vos accessoires&nbsp;:'
                            iconName={IconName.ACCESSORIES}
                            price={Math.abs(result.creditAccessories)}
                        />
                    )}
                </FlexBox>
            </FlexBox>
        );
    }

    if (result.status === EasyTradeInStatus.PARTIAL) {
        return (
            <>
                {!isTradeInConfirmed && (
                    <>
                        <div>
                            <div className='has-text-centered'>
                                <Icon name={IconName.INFOS_CIRCLE} size={IconSize.MEDIUM} />
                            </div>
                            <Text level={TextLevels.TWO} typo={TypographyAlign.TEXT_CENTERED} marginless>
                                Crédit restant à payer&nbsp;:
                            </Text>
                            <div className='has-text-centered'>
                                <InlinePrice level={TextLevels.ONE} amount={result.remainingCredit} />
                            </div>
                        </div>
                        <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Au vu de la valeur estimée de votre téléphone, on ne pourra clôturer qu&apos;une partie du
                            crédit en cours.
                        </Text>
                    </>
                )}

                <FlexBox wrap mobile>
                    {!isTradeInConfirmed && (
                        <FlexItem size={{ mobile: 12, desktop: undefined }}>
                            {result.remainingCredit > 0 && (
                                <InfoBox
                                    iconName={IconName.MOBILE_STAR}
                                    title='Valeur estimée de votre téléphone&nbsp;:'
                                    price={Math.abs(result.estimatedValue)}
                                />
                            )}
                        </FlexItem>
                    )}
                    <FlexItem>
                        <InfoBox
                            title={
                                isTradeInConfirmed
                                    ? 'Crédit restant à payer sur le téléphone\u00A0: '
                                    : 'Crédit restant à payer sur le téléphone (avant reprise)\u00A0: '
                            }
                            iconName={IconName.MOBILE}
                            price={isTradeInConfirmed ? result.remainingCreditTerminal : result.creditBeforeTradeIn}
                        />
                    </FlexItem>
                    {result.creditAccessories > 0 && (
                        <FlexItem>
                            <InfoBox
                                title='Crédit restant à payer sur vos accessoires&nbsp;:'
                                iconName={IconName.ACCESSORIES}
                                price={Math.abs(result.creditAccessories)}
                            />
                        </FlexItem>
                    )}
                </FlexBox>
            </>
        );
    }

    return (
        <>
            {!isTradeInConfirmed && (
                <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                    Malheureusement, nous ne pouvons pas reprendre votre téléphone en l&apos;état.
                </Text>
            )}
            <FlexBox>
                <InfoBox
                    title='Crédit restant à payer sur votre téléphone&nbsp;:'
                    iconName={IconName.MOBILE}
                    price={result.remainingCreditTerminal}
                />
                {result.creditAccessories > 0 && (
                    <InfoBox
                        title='Crédit restant à payer sur vos accessoires&nbsp;:'
                        iconName={IconName.ACCESSORIES}
                        price={Math.abs(result.creditAccessories)}
                    />
                )}
            </FlexBox>
        </>
    );
}

function InfoBox({
    iconName,
    title,
    price,
}: Readonly<{ iconName: IconName; title: string; price: number | undefined }>) {
    return (
        <Box shadowless className='is-fullwidth is-fullheight box-16'>
            <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <FlexBox direction={DirectionEnum.COLUMN} gap={2}>
                    <Icon name={iconName} size={IconSize.SMALLER} />
                    <Text level={TextLevels.TWO} marginless>
                        {title}
                    </Text>
                    {price !== undefined && <InlinePrice level={TextLevels.ONE} amount={price} />}
                </FlexBox>
            </BoxContent>
        </Box>
    );
}
